import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { ArrowRight } from 'lucide-react';

const EXAMPLE_EVENTS = [
  {
    id: 1,
    title: 'Traumhochzeit im Grünen',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80',
    description: 'Eine elegante Outdoor-Hochzeit mit romantischer Atmosphäre, floralen Akzenten und erstklassigem Service.',
    categories: [
      'Hochzeitsplaner',
      'Fotografen & Videografen',
      'Deko & Eventdesign',
      'Catering & Foodtrucks',
      'Musik & Entertainment',
      'Locations & Räume'
    ],
    budget: '10.000 - 25.000 €'
  },
  {
    id: 2,
    title: 'Moderne Firmenfeier',
    image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&q=80',
    description: 'Professionelles Firmenevent mit Networking, Live-Musik und exquisitem Catering für bis zu 200 Gäste.',
    categories: [
      'Eventplanung & Organisation',
      'Locations & Räume',
      'Catering & Foodtrucks',
      'Technik & Equipment-Verleih',
      'Musik & Entertainment',
      'Personal & Service'
    ],
    budget: '15.000 - 40.000 €'
  },
  {
    id: 3,
    title: 'Sommerfest mit Foodtrucks',
    image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80',
    description: 'Entspanntes Outdoor-Event mit Street-Food-Vibes, Lounge-Bereichen und Live-Musik unter freiem Himmel.',
    categories: [
      'Catering & Foodtrucks',
      'Musik & Entertainment',
      'Deko & Eventdesign',
      'Technik & Equipment-Verleih',
      'Locations & Räume'
    ],
    budget: '3.000 - 8.000 €'
  },
  {
    id: 4,
    title: 'Kindergeburtstag mit Animation',
    image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800&q=80',
    description: 'Unvergesslicher Kindergeburtstag mit professioneller Animation, Spielen, Hüpfburg und bunter Deko.',
    categories: [
      'Kinder- & Familien-Entertainment',
      'Catering & Foodtrucks',
      'Deko & Eventdesign',
      'Fotografen & Videografen'
    ],
    budget: '500 - 1.500 €'
  }
];

const Inspiration = ({ user, onLogout }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2]">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Hero Section */}
      <PageHero
        title="Event-Inspiration"
        subtitle="Lass dich von unseren Beispiel-Events inspirieren und finde die passenden Dienstleister für deine Veranstaltung"
        maxWidth="max-w-7xl"
      />
      
      <div className="pb-12 px-4">
        <div className="container max-w-7xl">

          <div className="grid lg:grid-cols-2 gap-8">
            {EXAMPLE_EVENTS.map((event) => (
              <div key={event.id} className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow" data-testid={`event-${event.id}`}>
                <div className="h-64 overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">{event.title}</h2>
                  <p className="text-gray-600 mb-6">{event.description}</p>
                  
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Benötigte Dienstleister:</p>
                    <div className="flex flex-wrap gap-2">
                      {event.categories.map((cat) => (
                        <span
                          key={cat}
                          className="px-3 py-1 bg-[#FFF0D1]/50 text-[#2B2142] rounded-lg text-sm font-medium cursor-pointer hover:bg-[#FFF0D1]"
                          onClick={() => navigate(`/search?category=${encodeURIComponent(cat)}`)}
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-sm text-gray-600">Beispiel-Budget:</p>
                      <p className="text-lg font-bold text-[#2B2142]">{event.budget}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => navigate('/event-konfigurator')}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl"
                    data-testid={`start-planning-${event.id}`}
                  >
                    Ähnliches Event planen
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-br from-[#2B2142] to-[#3D3158] rounded-3xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Bereit, dein Event zu planen?</h2>
            <p className="text-xl mb-8 opacity-90">
              Nutze unseren Event-Konfigurator und finde in wenigen Minuten passende Dienstleister
            </p>
            <button
              onClick={() => navigate('/event-konfigurator')}
              className="px-8 py-4 bg-white text-[#2B2142] font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              data-testid="cta-configurator"
            >
              Zum Event-Konfigurator
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Inspiration;
